import { useRef, useState } from 'react'
import { Box, Button, Checkbox, Divider, IconButton, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { SPACING } from '@common/theme/spacing'

interface ModalityViewerSelectorProps {
  modalities: string[]
  modalitiesWithData: string[]
  onOpenViewer: (selectedModalities: string[]) => void
  disabled?: boolean
}

export default function ModalityViewerSelector({
  modalities,
  modalitiesWithData,
  onOpenViewer,
  disabled,
}: ModalityViewerSelectorProps) {
  const [selectedModality, setSelectedModality] = useState<string[] | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const open = Boolean(anchorEl)

  const effectiveSelection = selectedModality ?? modalitiesWithData

  const handleToggle = () => {
    setAnchorEl(open ? null : containerRef.current)
  }

  const handleModalityToggle = (mod: string) => {
    const current = effectiveSelection
    setSelectedModality(current.includes(mod) ? current.filter((m) => m !== mod) : [...current, mod])
  }

  return (
    <Box
      ref={containerRef}
      display="flex"
      alignItems="stretch"
      flexShrink={0}
      sx={{
        border: '1px solid',
        borderColor: 'secondary.dark',
        borderRadius: (theme) => theme.spacing(SPACING.borderRadiusSm),
        overflow: 'hidden',
      }}
    >
      <Button
        disabled={disabled || effectiveSelection.length === 0}
        onClick={() => onOpenViewer(effectiveSelection)}
        sx={{
          color: 'text.primary',
          border: 'none',
          py: SPACING.spacingMd,
          px: SPACING.spacingLg,
          borderRadius: 0,
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        Open in viewer
      </Button>
      <Divider orientation="vertical" flexItem sx={{ borderColor: 'secondary.dark', my: 1 }} />
      <Tooltip title="Select modalities for viewing">
        <IconButton
          size="small"
          disabled={disabled}
          onClick={handleToggle}
          sx={{
            borderRadius: 0,
            px: SPACING.spacingSm,
          }}
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5 } } }}
      >
        <MenuItem
          onClick={() => {
            const allSelected = modalitiesWithData.every((m) => effectiveSelection.includes(m))
            setSelectedModality(allSelected ? [] : [...modalitiesWithData])
          }}
          sx={{ py: 1 }}
        >
          <Checkbox
            size="small"
            checked={modalitiesWithData.length > 0 && modalitiesWithData.every((m) => effectiveSelection.includes(m))}
            indeterminate={
              effectiveSelection.length > 0 && !modalitiesWithData.every((m) => effectiveSelection.includes(m))
            }
            disableRipple
            sx={{ p: 0, mr: 1 }}
          />
          <ListItemText>Select all</ListItemText>
        </MenuItem>
        <Divider />
        {modalities.map((mod) => {
          const hasData = modalitiesWithData.includes(mod)
          return (
            <MenuItem key={mod} disabled={!hasData} onClick={() => handleModalityToggle(mod)} sx={{ py: 1 }}>
              <Checkbox size="small" checked={effectiveSelection.includes(mod)} disableRipple sx={{ p: 0, mr: 1 }} />
              <ListItemText>{mod}</ListItemText>
            </MenuItem>
          )
        })}
      </Menu>
    </Box>
  )
}
